import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Steps,
    Space
} from 'antd';
import {authService, customerService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalViewItem from "../../components/HorizontalItems/ViewItem";

// Функция для форматирования даты в формате "1 июля в 23:11"
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';

    const date = new Date(dateString);
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} в ${hours}:${minutes}`;
};

// Функция для форматирования ФИО
const formatFullName = (l_name, f_name, m_name) => {
    const parts = [l_name, f_name, m_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Не указано';
};

const {Title, Text} = Typography;

const CustomerPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [Customer, setCustomer] = useState(null);

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр кассира');

        setRightContent(
            authService.hasPermission('pos:customers:edit') && (
                <Button
                    type="primary"
                    onClick={() => navigate(`/customers/${id}/edit`)}
                >
                    Редактировать
                </Button>
            )
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCustomer(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const CustomerResponse = await customerService.getCustomerById(id);

            if (CustomerResponse.status === 'success') {
                setCustomer(CustomerResponse.data);
                console.log('Получены данные кассира:', CustomerResponse.data);
            } else {
                message.error('Не удалось загрузить данные кассира');
                navigate('/Customers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Кассир не найден",
                    description: "Указанный кассир не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных кассира. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Клиент «{formatFullName(Customer.first_name, Customer.last_name)}»
            </Title>
            <Space>
                <Text>ID: <Text>{Customer.id}</Text></Text>
            </Space>

            <Tabs defaultActiveKey="data" items={[
                {
                    key: "data",
                    label: "Основная информация",
                    children: (
                        <Row gutter={24}>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Личные данные</Title>

                                    <HorizontalViewItem label="Фамилия">
                                        <Text>{Customer.last_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Имя">
                                        <Text>{Customer.first_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Номер карты">
                                        <Text copyable>{Customer.card_number || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Телефон">
                                        <Text>{Customer.phone || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Почта">
                                        <Text>{Customer.email || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Баланс">
                                        <Text>{Customer.balance || 'Отсутствует'}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Системная информация</Title>

                                    <HorizontalViewItem label="Дата создания">
                                        <Text>{formatDate(Customer.created_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Сотрудник">
                                        <Text>
                                            {Customer.is_employee === 1 ? 'Да' : Customer.is_employee === 0 ? 'Нет' : 'Не указано'}
                                        </Text>
                                    </HorizontalViewItem>

                                    {Customer.is_employee === 1 && (
                                        <HorizontalViewItem label="Баланс сотрудника на день">
                                            <Text>{Customer.employee_day_balance ?? 'Не указано'}</Text>
                                        </HorizontalViewItem>
                                    )}
                                </Space>
                            </Col>
                        </Row>
                    )
                },
                {
                    key: "history",
                    label: "Транзакции",
                    children: (
                        <Card>
                            {Customer.transactions && Customer.transactions.length > 0 ? (
                                <Steps
                                    direction="vertical"
                                    size="small"
                                    current={Customer.transactions.length}
                                    progressDot
                                    items={Customer.transactions.map(transaction => ({
                                        title: (
                                            <Space>
                                                <Text>Транзакция #{transaction.id}</Text>
                                                {transaction.amount}
                                            </Space>
                                        ),
                                        description: (
                                            <Space direction="vertical" size={4}>
                                                <Text type="secondary">
                                                    {formatDate(transaction.created_at)}
                                                </Text>
                                                {transaction.sale_id && (
                                                    <Text type="secondary">
                                                        <a
                                                            href={`/sales/${transaction.sale_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Продажа #{transaction.sale_id}
                                                        </a>
                                                    </Text>
                                                )}
                                            </Space>
                                        )
                                    }))}
                                />
                            ) : (
                                <Text type="secondary">Транзакции отсутствуют</Text>
                            )}
                        </Card>
                    )
                }
            ]}/>
        </Card>
    );
}

export default CustomerPage;